import { Grid, Typography, Box } from "@mui/material";
import React from "react";
import AppWrapperContainer from "../../../components/AppWrapperContainer";
import {
  loadBlogDataBySlug,
  loadBlogDataByTags,
} from "../../../lib/loadServerData";
import Icon_LinkedInblog from "../../../public/assets/images/home/more/blog/Icon_LinkedInblog.webp";
import Icon_Facebookblog from "../../../public/assets/images/home/more/blog/Icon_Facebookblog.webp";
import Icon_Twitterblog from "../../../public/assets/images/home/more/blog/Icon_Twitterblog.webp";
import Image from "next/image";
import { BLOG } from "../../../interface/Interface";
import Link from "next/link";
import AppFooter from "../../../components/AppFooter";
import Blogs from "../../../components/home/8-blogs/Blogs";
import Seo from "../../../components/Seo";

export async function getServerSideProps({ params }: { params: any }) {
  const slug = params?.slug;
  if (!slug || typeof slug !== "string") {
    return { notFound: true };
  }

  const blogData = await loadBlogDataBySlug(slug);
  const post = blogData?.posts?.length ? blogData.posts[0] : null;

  if (!post) {
    return { notFound: true };
  }

  let relBlogs: { posts: any[] } = { posts: [] };
  const tagSlug =
    post.primary_tag?.slug ||
    (Array.isArray(post.tags) ? post.tags?.[0]?.slug : undefined);
  if (tagSlug) {
    relBlogs = await loadBlogDataByTags(tagSlug);
  }

  return {
    props: {
      blog: post,
      relBlogs: relBlogs?.posts || [],
    },
  };
}

function Slug(props: any) {
  const blog: BLOG = props.blog;

  if (!blog) return null;

  const shareUrl =
    typeof window !== "undefined" && window?.location?.href
      ? window.location.href
      : blog.canonical_url;

  const relBlogs = (props.relBlogs || []).filter(
    (item: any) => item?.slug && item.slug !== blog.slug
  );

  const updateInnerHtml = (html: string) => {
    const text = html
      .replaceAll(
        'href="https://play',
        `id='onlyHrefRedirectionBlog' href="https://`
      )
      .replaceAll("href", `id='onlyHrefBlog' href`);
    return { __html: text };
  };

  const siteUrl =
    process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/+$/, "") ||
    (() => {
      try {
        return blog?.canonical_url ? new URL(blog.canonical_url).origin : "https://gromo.in";
      } catch {
        return "https://gromo.in";
      }
    })();

  const articleJsonLd = blog
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "@id": `${blog.canonical_url}#blogposting`,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": blog.canonical_url,
        },
        headline: blog.title,
        description:
          blog.meta_description || blog.custom_excerpt || blog.excerpt || undefined,
        image: blog.feature_image ? [blog.feature_image] : undefined,
        datePublished: blog.published_at,
        dateModified: blog.updated_at || blog.published_at,
        author: blog.primary_author?.name
          ? {
              "@type": "Person",
              name: blog.primary_author.name,
              url: blog.primary_author.url,
            }
          : undefined,
        publisher: {
          "@id": `${siteUrl}/#organization`,
        },
        keywords: Array.isArray(blog.tags)
          ? blog.tags
              .filter((_, index, currArr) => index < currArr.length - 1)
              .map((t) => t.name)
              .join(",")
          : undefined,
      }
    : null;

  return (
    <>
      {blog ? (
        <Seo
          title={blog.meta_title || blog.title}
          description={blog.meta_description || blog.custom_excerpt || undefined}
          keywords={blog.tags
            .filter((_, index, currArr) => index < currArr.length - 1)
            .map((t) => t.name)
            .join(",")}
          openGraph={{
            title: blog.og_title || blog.meta_title || blog.title,
            description:
              blog.og_description ||
              blog.meta_description ||
              blog.custom_excerpt ||
              undefined,
            type: "article",
            image: blog.og_image?.toString?.() || blog.feature_image,
          }}
          twitter={{
            title: blog.twitter_title || blog.meta_title || blog.title,
            description:
              blog.twitter_description ||
              blog.meta_description ||
              blog.custom_excerpt ||
              undefined,
            image: blog.twitter_image || blog.feature_image,
          }}
          jsonLd={articleJsonLd ? [articleJsonLd] : undefined}
        />
      ) : (
        <></>
      )}
      <AppWrapperContainer nav navBgColor="white">
        {blog ? (
          <>
            <Grid
              sx={{
                minHeight: { xs: "40vh", md: "50vh" },
                background:
                  "radial-gradient(37.66% 109.51% at 50% 50%, #718DD9 0%, #4666BD 100%)",
                color: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "28px", md: "64px" },
                  lineHeight: { xs: "36px", md: "72px" },
                  width: { xs: "80%", md: "60%" },
                  textAlign: "center",
                }}
              >
                {blog.title}
              </Typography>
            </Grid>
            <>
              <Grid
                sx={{ width: { xs: "90%", md: "80%" }, margin: "20px auto" }}
              >
                <Grid
                  sx={{
                    width: { xs: "100%x", md: "550px" },
                    margin: "63px auto",
                  }}
                >
                  <Image
                    src={blog.feature_image}
                    alt={blog.custom_excerpt || "GroMo"}
                    width={100}
                    height={100}
                    style={{
                      objectFit: "contain",
                      width: "100%",
                      height: "100%",
                    }}
                    loading="lazy"
                  />
                </Grid>
              </Grid>

              <Grid
                sx={{
                  width: { xs: "90%", md: "80%" },
                  margin: "auto",
                  maxWidth: "1440px",
                }}
              >
                <Typography
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "24px", md: "36px" },
                    lineHeight: { xs: "32px", md: "38px" },
                    color: "#0A0C0F",
                    margin: "10px 0",
                  }}
                >
                  {blog.title}
                </Typography>
                <span
                  dangerouslySetInnerHTML={updateInnerHtml(blog.html)}
                ></span>
              </Grid>

              <Grid
                sx={{
                  width: { xs: "90%", md: "80%" },
                  margin: "20px auto",
                  display: { xs: "block", md: "flex" },
                  justifyContent: { xs: "center", md: "space-between" },
                  alignItems: "center",
                  maxWidth: "1440px",
                }}
              >
                <Grid
                  sx={{
                    display: "flex",
                    justifyContent: { md: "space-between" },
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      textAlign: { md: "left" },
                      margin: { xs: "5px 0", md: "0" },
                      fontWeight: 700,
                    }}
                  >
                    share
                  </Typography>
                  {[
                    {
                      icon: Icon_Facebookblog,
                      link: `http://www.facebook.com/sharer.php?u=${shareUrl}`,
                    },
                    {
                      icon: Icon_LinkedInblog,
                      link: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
                    },
                    {
                      icon: Icon_Twitterblog,
                      link: `https://twitter.com/share?url=${shareUrl}`,
                    },
                  ].map((item, i) => {
                    return (
                      <Box
                        sx={{
                          height: "32px",
                          width: "32px",
                          margin: "0px 4px",
                        }}
                        key={i}
                      >
                        <Link href={item.link} rel="preload">
                          <Image
                            src={item.icon}
                            alt={`item${i}`}
                            loading="lazy"
                          />
                        </Link>
                      </Box>
                    );
                  })}
                </Grid>
                <Grid
                  sx={{
                    display: "flex",
                    alignItems: { md: "center" },
                    justifyContent: { xs: "center", md: "normal" },
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ whiteSpace: "nowrap", margin: { xs: "8px 0" } }}
                  >
                    Tags :-
                  </Typography>
                  <Grid
                    sx={{
                      display: "flex",
                      flexWrap: { xs: "wrap", md: "nowrap" },
                    }}
                  >
                    {blog.tags
                      .filter((_, index, currArr) => index < currArr.length - 1)
                      .map((item) => {
                        return (
                          <Typography
                            sx={{
                              padding: "4px 12px",
                              fontSize: { xs: "12px", md: "14px" },
                              borderRadius: "50px",
                              margin: { xs: "4px 8px", md: "4px" },
                              backgroundColor: "#4d71d2",
                              color: "white",
                            }}
                            key={item.id}
                          >
                            {item.name}
                          </Typography>
                        );
                      })}
                  </Grid>
                </Grid>
              </Grid>
              <Grid sx={{ margin: "auto", width: "80%" }}>
                {relBlogs && relBlogs.length ? (
                  <Blogs
                    relBlogs={
                      relBlogs.length === 3 ? relBlogs : relBlogs.slice(0, 3)
                    }
                    heading="Related Blogs"
                  />
                ) : null}
              </Grid>
            </>
          </>
        ) : null}
        <AppFooter />
      </AppWrapperContainer>
    </>
  );
}
export default Slug;
